import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Stack,
  Tag,
  Text,
  IconButton,
  Badge,
  useDisclosure,
  Skeleton,
} from "@chakra-ui/react";
import {
  FaGithub,
  FaLinkedin,
  FaWhatsapp,
  FaEnvelope,
  FaArrowRight,
  FaDownload,
} from "react-icons/fa";
import { useEffect, useState } from "react";
import { useProfile, pick } from "../hooks/useContent";
import { useLanguage } from "../context/LanguageContext";
import CvPreviewModal from "../pdf/CvPreviewModal";
import { HeroSkeleton } from "./ui/Skeletons";

export default function Hero() {
  const { language } = useLanguage();
  const { data, loading } = useProfile();
  const [photoReady, setPhotoReady] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  // Fallback so the hero never renders blank before the profile is seeded.
  const p = data || {
    name: language === "es" ? "Tu Nombre" : "Your Name",
    role_es: "Tu rol profesional",
    role_en: "Your professional role",
    about_es:
      "Agrega tu información desde el panel de administración (/admin).",
    about_en: "Add your information from the admin panel (/admin).",
    available: false,
    github: "#",
    linkedin: "#",
    email: "",
    whatsapp: "",
    photo_url: "",
  };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  const initials = (p.name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  useEffect(() => {
    setPhotoReady(!p.photo_url);
    setPhotoError(false);
  }, [p.photo_url]);

  if (loading) return <HeroSkeleton />;

  return (
    <Box
      as="section"
      id="hero"
      pt={{ base: 28, md: 36 }}
      pb={{ base: 12, md: 20 }}
    >
      <Container maxW="6xl">
        <Flex direction={{ base: "column", md: "row" }} align="center" gap={12}>
          <Stack flex="1" spacing={6}>
            <Tag
              w="fit-content"
              bgGradient="linear(to-r, yellow.700, transparent)"
              color="white"
              rounded="full"
              px={4}
            >
              👋 {language === "es" ? "Hola, soy" : "Hi, I'm"}
            </Tag>
            <Heading
              fontSize={{ base: "4xl", md: "6xl" }}
              color="text.heading"
              lineHeight="1.05"
            >
              {p.name}
              <Text
                bgGradient="linear(to-r, brand.400, brand.500)"
                bgClip="text"
              >
                {pick("role", p, language)}
              </Text>
            </Heading>
            <HStack spacing={4}>
              <Button
                id="download-cv"
                colorScheme="blue"
                rightIcon={<FaDownload />}
                onClick={onOpen}
              >
                {language === "es"
                  ? "Ver / Descargar CV"
                  : "View / Download CV"}
              </Button>
              <Button
                variant="outline"
                colorScheme="blue"
                rightIcon={<FaArrowRight />}
                onClick={() => scrollTo("#projects")}
              >
                {language === "es" ? "Ver Proyectos" : "View Projects"}
              </Button>
            </HStack>
            <HStack spacing={3}>
              <IconButton
                as="a"
                href={p.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                icon={<FaGithub />}
                rounded="full"
                variant="ghost"
              />
              <IconButton
                as="a"
                href={p.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                icon={<FaLinkedin />}
                rounded="full"
                variant="ghost"
              />
              <IconButton
                as="a"
                href={`mailto:${p.email}`}
                rel="noopener noreferrer"
                aria-label="Email"
                icon={<FaEnvelope />}
                rounded="full"
                variant="ghost"
              />
              <IconButton
                as="a"
                href={`https://wa.me/${p.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                icon={<FaWhatsapp />}
                rounded="full"
                variant="ghost"
              />
            </HStack>
          </Stack>
          <Box flex="1" position="relative" maxW="sm">
            <Box
              rounded="2xl"
              overflow="hidden"
              border="1px solid"
              borderColor="border.card"
              bg="bg.card"
              position="relative"
            >
              {p.photo_url ? (
                <>
                  {!photoReady && !photoError && (
                    <Skeleton
                      data-testid="hero-photo-skeleton"
                      position="absolute"
                      inset={0}
                      zIndex={1}
                      h="320px"
                      w="100%"
                      borderRadius="2xl"
                      startColor="gray.700"
                      endColor="gray.800"
                    />
                  )}
                  <Image
                    src={p.photo_url}
                    alt={p.name}
                    w="full"
                    h="320px"
                    objectFit="cover"
                    opacity={photoReady || photoError ? 1 : 0}
                    transition="opacity 0.25s ease"
                    onLoad={() => setPhotoReady(true)}
                    onError={() => {
                      setPhotoReady(true);
                      setPhotoError(true);
                    }}
                  />
                </>
              ) : (
                <Flex minH="320px" align="center" justify="center" bg="bg.card">
                  <Text
                    fontSize="5xl"
                    fontWeight="bold"
                    color="gray.500"
                    letterSpacing="wide"
                  >
                    {initials || "?"}
                  </Text>
                </Flex>
              )}
              {photoError && (
                <Flex
                  position="absolute"
                  inset={0}
                  minH="320px"
                  align="center"
                  justify="center"
                  bg="bg.card"
                >
                  <Text
                    fontSize="5xl"
                    fontWeight="bold"
                    color="gray.500"
                    letterSpacing="wide"
                  >
                    {initials || "?"}
                  </Text>
                </Flex>
              )}
            </Box>
            {p.available && (
              <Box
                position="absolute"
                bottom={4}
                left={4}
                right={4}
                bg="bg.card"
                border="1px solid"
                borderColor="border.card"
                rounded="lg"
                p={3}
              >
                <HStack>
                  <Badge colorScheme="green" rounded="full">
                    ●
                  </Badge>
                  <Box>
                    <Text color="text.heading" fontWeight="bold" fontSize="sm">
                      {language === "es"
                        ? "Disponible para proyectos"
                        : "Available for projects"}
                    </Text>
                    <Text color="text.muted" fontSize="xs">
                      {pick("available_text", p, language)}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            )}
          </Box>
        </Flex>
      </Container>
      <CvPreviewModal isOpen={isOpen} onClose={onClose} language={language} />
    </Box>
  );
}
